import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { BRAND } from "../data/collection";
import { IG_DM, buildGeneralMessage, waLink } from "../lib/order";
import { WhatsAppMark, ReplyNudge } from "../components/site/QuickChat";
import { Reveal, MaskLine, EASE } from "../components/site/Reveal";

const STORY = [
  "I'm Anjali, the founder and designer of Keilani Clothing. With over 14 years in the fashion industry, I've had the opportunity to work with four fashion companies — gaining deep experience across apparel design, product development, garment construction, fabric selection, and truly understanding what a customer needs.",
  "One of the proudest milestones of my career was designing costumes for the Millennial Beauty Pageant — an experience that challenged my creativity and strengthened my confidence as a designer.",
  "I started Keilani Clothing in 2022, initially as a kidswear brand. As the brand evolved, so did my vision. Today, Keilani creates women's clothing that blends comfort, simplicity and timeless style.",
  "For me, Keilani is more than a clothing label — it's a reflection of my journey, my creativity, and my commitment to quality. Every collection is designed with the hope that the women who wear Keilani feel confident, beautiful, and completely themselves.",
];

const STANDS_FOR = [
  { no: "01", t: "Comfort", d: "Breathable natural cottons that feel as good as they look." },
  { no: "02", t: "Simplicity", d: "Quiet, minimal design that lets the wearer shine." },
  { no: "03", t: "Craft", d: "Hand-embroidered detailing, made slow in small batches." },
  { no: "04", t: "Quality", d: "Thoughtfully made — one garment at a time." },
];

export default function About() {
  const navigate = useNavigate();

  return (
    <div data-testid="about-page">
      {/* hero */}
      <section className="relative min-h-[70vh] overflow-hidden bg-[#050505] text-white flex items-end">
        <div className="kln-noise" />
        <div className="absolute right-0 top-0 h-full w-full md:w-[42vw]">
          <img
            src="/anjali.jpg"
            alt="Anjali — Founder & Designer, Keilani Clothing"
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/40 to-transparent md:via-[#050505]/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 to-transparent" />
          <span className="absolute bottom-5 right-5 z-10 font-body text-[10px] uppercase tracking-[0.28em] text-white/70">
            Anjali · Founder &amp; Designer
          </span>
        </div>

        <div className="relative z-10 px-6 md:px-12 lg:px-20 pb-16 pt-40 w-full">
          <button
            data-testid="about-back"
            onClick={() => navigate("/")}
            className="group mb-10 flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.28em] text-white/60 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={1.3} />
            Back home
          </button>
          <p className="mb-5 font-body text-[11px] uppercase tracking-[0.4em] text-white/50">
            <span className="text-[var(--kln-gold)]">The Story</span> · Est. 2022
          </p>
          <h1 className="font-display font-light leading-[0.9] text-white">
            <MaskLine delay={0.1} className="text-6xl md:text-8xl lg:text-9xl">
              Keilani,
            </MaskLine>
            <MaskLine delay={0.24} className="text-5xl md:text-7xl lg:text-8xl font-serif-editorial italic text-white/70">
              by Anjali.
            </MaskLine>
          </h1>
        </div>
      </section>

      {/* brand quote */}
      <section className="bg-[#FDFBF7] text-[#050505]">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 py-24 md:py-32 text-center">
          <Reveal>
            <span className="font-serif-editorial text-5xl leading-none text-[var(--kln-gold)]">&ldquo;</span>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="mt-4 font-serif-editorial text-2xl md:text-4xl font-light italic leading-[1.3] text-black/80">
              {BRAND.quote}
            </p>
          </Reveal>
        </div>
      </section>

      {/* story */}
      <section className="bg-white text-[#050505]">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20 py-24 md:py-32">
          <div className="grid gap-12 md:grid-cols-12 md:gap-16">
            <div className="md:col-span-5">
              <div className="md:sticky md:top-32">
                <Reveal>
                  <p className="font-body text-[11px] uppercase tracking-[0.4em] text-black/40">
                    From the designer
                  </p>
                </Reveal>
                <Reveal delay={0.05}>
                  <h2 className="mt-5 font-display text-5xl md:text-6xl font-light leading-[0.95]">
                    A dream that
                    <br />
                    <span className="font-serif-editorial italic text-black/70">keeps growing.</span>
                  </h2>
                </Reveal>
                <Reveal delay={0.1}>
                  <figure className="mt-8 overflow-hidden">
                    <img
                      src="/anjali.jpg"
                      alt="Anjali, founder of Keilani Clothing"
                      className="w-full max-w-sm object-cover"
                    />
                    <figcaption className="mt-3 font-body text-[10px] uppercase tracking-[0.26em] text-black/40">
                      Anjali · Founder &amp; Designer
                    </figcaption>
                  </figure>
                </Reveal>
              </div>
            </div>
            <div className="md:col-span-7">
              {STORY.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <p className="mb-8 font-body text-lg md:text-xl font-light leading-relaxed text-black/70">
                    {p}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* what keilani stands for */}
      <section className="bg-[#050505] text-white relative overflow-hidden">
        <div className="kln-noise" />
        <div className="relative mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20 py-24 md:py-32">
          <Reveal>
            <p className="font-body text-[11px] uppercase tracking-[0.4em] text-white/40">
              What Keilani stands for
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 mb-14 font-display text-5xl md:text-7xl font-light leading-[0.92]">
              Four things, always.
            </h2>
          </Reveal>
          <div className="grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {STANDS_FOR.map((s, i) => (
              <Reveal key={s.no} delay={i * 0.06}>
                <div className="border-t border-white/15 pt-6">
                  <span className="font-serif-editorial text-3xl font-light text-white/30">{s.no}</span>
                  <h3 className="mt-3 font-display text-3xl font-light">{s.t}</h3>
                  <p className="mt-3 font-body text-sm font-light leading-relaxed text-white/55">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-16 flex flex-wrap gap-3">
              <motion.a
                data-testid="about-shop-whatsapp"
                href={waLink(buildGeneralMessage())}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 rounded-full bg-[#25D366] px-8 py-4 font-body text-[12px] uppercase tracking-[0.24em] text-white transition-transform duration-300 hover:brightness-110 hover:translate-y-[-1px]"
                transition={{ ease: EASE }}
              >
                <WhatsAppMark className="h-4 w-4" />
                WhatsApp us
              </motion.a>
              <motion.a
                data-testid="about-shop-cta"
                href={IG_DM}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 rounded-full bg-white px-8 py-4 font-body text-[12px] uppercase tracking-[0.24em] text-black transition-colors duration-300 hover:bg-[var(--kln-gold)]"
                transition={{ ease: EASE }}
              >
                Instagram DM
                <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
              </motion.a>
            </div>
            <ReplyNudge testid="about-wa-nudge" className="mt-3" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
